/**
 * Shell top bar (Phase 8 §3 I/J/K).
 *
 * Renders only: breadcrumb (left) + credits pill + notifications + agent
 * button + account menu (right). Height locked to 48 px. No search input,
 * no page title.
 */
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, ChevronRight } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { useShellBreadcrumbs, type BreadcrumbSegment } from './useBreadcrumbs';
import { TopbarNotifications } from './TopbarNotifications';
import { TopbarAccountMenu } from './TopbarAccountMenu';
import { TopbarCreditsPill } from './TopbarCreditsPill';
import { FeedbackButton } from '@shared/components/FeedbackButton';

interface TopbarProps {
  onMenuClick: () => void;
}

function renderCrumb(seg: BreadcrumbSegment, i: number) {
  const showChevron = i > 0;
  const key = `${i}-${seg.kind}-${'to' in seg ? seg.to : ''}-${'label' in seg ? seg.label : ''}`;
  return (
    <li key={key} className="flex min-w-0 max-w-full items-center gap-2">
      {showChevron ? (
        <ChevronRight className="h-4 w-4 flex-shrink-0 vscode-text-tertiary" aria-hidden />
      ) : null}
      {seg.kind === 'link' ? (
        <Link to={seg.to} className="truncate vscode-text-secondary transition-colors hover:vscode-text-primary">
          {seg.label}
        </Link>
      ) : seg.kind === 'ellipsis' ? (
        <span className="flex-shrink-0 vscode-text-tertiary" aria-hidden>
          …
        </span>
      ) : seg.kind === 'skeleton' ? (
        <span className="block h-4 w-40 max-w-[50vw] animate-pulse rounded vscode-bg-tertiary" aria-busy="true" />
      ) : seg.kind === 'error' ? (
        <span className="truncate text-[13px] vscode-text-tertiary">{seg.label}</span>
      ) : (
        <span className="min-w-0 truncate font-semibold vscode-text-primary">
          {seg.label}
        </span>
      )}
    </li>
  );
}

export function Topbar({ onMenuClick }: TopbarProps) {
  const { isLoading } = useAuth();
  const location = useLocation();
  const { segments } = useShellBreadcrumbs(location.pathname);
  const [notifOpen, setNotifOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  // Sourcing pages show a plain "Sourcing" title instead of the breadcrumb trail.
  const isSourcing = location.pathname.startsWith('/sourcing');

  return (
    <header className="gg-app-topbar relative z-[100] flex h-12 min-h-[48px] max-h-12 flex-shrink-0 items-center gap-4 px-4 vscode-transition sm:px-5">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <button
          type="button"
          onClick={onMenuClick}
          className="flex-shrink-0 rounded-[8px] p-2 vscode-text-tertiary transition-colors hover:vscode-bg-hover hover:vscode-text-primary lg:hidden"
          aria-label="Open menu"
        >
          <Menu className="gg-toolbar-ic gg-toolbar-ic--primary h-5 w-5" />
        </button>

        {isSourcing ? (
          <span className="text-[13px] font-semibold vscode-text-primary">Sourcing</span>
        ) : (
          <nav className="min-w-0 flex-1 overflow-hidden" aria-label="Breadcrumb">
            {isLoading ? (
              <div className="flex items-center gap-2" aria-busy="true" aria-label="Loading header">
                <div className="h-4 w-24 animate-pulse rounded vscode-bg-tertiary" />
                <div className="hidden h-4 w-16 animate-pulse rounded vscode-bg-tertiary sm:block" />
              </div>
            ) : (
              <ol className="flex min-w-0 flex-nowrap items-center gap-2 overflow-hidden text-[13px] whitespace-nowrap">
                {segments.map((s, i) => renderCrumb(s, i))}
              </ol>
            )}
          </nav>
        )}
      </div>

      <div className="flex flex-shrink-0 items-center gap-1.5 sm:gap-2">
        <TopbarCreditsPill />
        <FeedbackButton />
        <TopbarNotifications
          open={notifOpen}
          onOpenChange={(v) => {
            setNotifOpen(v);
            if (v) setAccountOpen(false);
          }}
        />
        <TopbarAccountMenu
          open={accountOpen}
          onOpenChange={(v) => {
            setAccountOpen(v);
            if (v) setNotifOpen(false);
          }}
        />
      </div>
    </header>
  );
}
